import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = not authenticated, object = user
  const [user, setUser] = useState(null);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  const register = async (name, email, password, workspaceName) => {
    const { data } = await api.post("/auth/register", {
      name,
      email,
      password,
      workspace_name: workspaceName,
    });
    // Pending accounts can't log in until a super admin activates them, so we
    // do NOT set the user state here — caller (AuthPage) shows a pending
    // confirmation screen instead.
    if (data?.pending_approval) {
      return data;
    }
    if (data.workspace_id) {
      localStorage.setItem("probe_workspace_id", data.workspace_id);
    }
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      localStorage.removeItem("probe_workspace_id");
      setUser(false);
    }
  };

  const refreshUser = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  };

  const emulate = async (userId) => {
    // Per product decision: keep the currently selected workspace id. The
    // WorkspaceContext will auto-fall-back to the emulated user's first
    // workspace if the stored id is not in their accessible list.
    const { data } = await api.post(`/admin/emulate/${userId}`);
    setUser(data);
    return data;
  };

  /** Workspace-owner emulation. The originating session must be an owner of
   * the given workspace and the target must be a non-pending, non-super-admin
   * member of that same workspace. Re-uses the JWT-with-emulator_id mechanism. */
  const emulateAs = async (workspaceId, userId) => {
    const { data } = await api.post(`/workspaces/${workspaceId}/emulate/${userId}`);
    setUser(data);
    return data;
  };

  const endEmulation = async () => {
    const { data } = await api.post("/auth/end-emulation");
    setUser(data);
    return data;
  };

  return (
    <AuthContext.Provider
      value={{ user, login, register, logout, emulate, emulateAs, endEmulation, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
